// src/composition.ts
import { getCurrentInstance, inject } from "vue-demi";

// src/util/warn.ts
function assert(condition, message) {
  if (!condition)
    throw new Error(`[fluent-vue] ${message}`);
}
function warn(message, ...args) {
  console.warn(`[fluent-vue] ${message}`, ...args);
}

// src/symbols.ts
var RootContextSymbol = Symbol("root-context");

// src/getContext.ts
import { computed } from "vue-demi";
import { CachedSyncIterable } from "cached-iterable";

// src/TranslationContext.ts
import { mapBundleSync } from "@fluent/sequence";
var TranslationContext = class {
  constructor(bundles, options) {
    this.options = options;
    this.format = (key, value) => {
      var _a;
      const context = this.getBundle(key);
      const message = this.getMessage(context, key);
      return (_a = this._format(context, message, value)) != null ? _a : key;
    };
    this.formatAttrs = (key, value) => {
      var _a;
      const context = this.getBundle(key);
      const message = this.getMessage(context, key);
      return (_a = this._formatAttrs(context, message, value)) != null ? _a : {};
    };
    this.formatWithAttrs = (key, value) => {
      var _a;
      const context = this.getBundle(key);
      const message = this.getMessage(context, key);
      const formatValue = this._format(context, message, value);
      return {
        value: formatValue != null ? formatValue : key,
        attributes: (_a = this._formatAttrs(context, message, value)) != null ? _a : {},
        hasValue: formatValue !== null
      };
    };
    this.$t = this.format;
    this.$ta = this.formatAttrs;
    this.bundles = bundles;
  }
  getBundle(key) {
    return mapBundleSync(this.bundles.value, key);
  }
  getMessage(bundle, key) {
    const message = bundle == null ? void 0 : bundle.getMessage(key);
    if (message === void 0) {
      this.options.warnMissing(key);
      return null;
    }
    return message;
  }
  formatPattern(bundle, key, message, value) {
    const errors = [];
    const formatted = bundle.formatPattern(message, value, errors);
    for (const error of errors)
      warn(`Error when formatting message with key [${key}]`, error);
    return formatted;
  }
  _format(context, message, value) {
    if (context === null || message === null || message.value === null)
      return null;
    return this.formatPattern(context, message.id, message.value, value);
  }
  _formatAttrs(context, message, value) {
    if (context === null || message === null)
      return null;
    const result = {};
    for (const [attrName, attrValue] of Object.entries(message.attributes))
      result[attrName] = this.formatPattern(context, message.id, attrValue, value);
    return result;
  }
};

// src/inheritBundle.ts
import { FluentBundle } from "@fluent/bundle";
function inheritBundle(locale, parent) {
  const bundle = new FluentBundle(locale, {
    functions: parent._functions,
    useIsolating: parent._useIsolating,
    transform: parent._transform
  });
  bundle._terms = new Map(parent._terms);
  bundle._messages = new Map(parent._messages);
  return bundle;
}

// src/getContext.ts
function* flatMap(iterable, mapper) {
  for (const item of iterable)
    yield* mapper(item);
}
function getContext(rootContext, instance, fromSetup = false) {
  if (instance == null)
    return rootContext;
  const options = instance.$options;
  if (options._fluent != null)
    return options._fluent;
  const context = getMergedContext(rootContext, options.fluent);
  const isServer = typeof window === "undefined";
  if (!fromSetup && !isServer)
    options._fluent = context;
  return context;
}
function getMergedContext(rootContext, fluent) {
  if (fluent == null)
    return rootContext;
  const overriddenBundles = computed(
    () => CachedSyncIterable.from(
      flatMap(rootContext.bundles.value, (parentBundle) => Object.entries(fluent).map(([locale, resources]) => {
        const locales = locale.split(/[\s+,]/);
        const matchingLocales = parentBundle.locales.filter((bundleLocale) => locales.includes(bundleLocale));
        if (matchingLocales.length === 0)
          return parentBundle;
        const bundle = inheritBundle(locales, parentBundle);
        bundle.addResource(resources, { allowOverrides: true });
        return bundle;
      }))
    )
  );
  return new TranslationContext(overriddenBundles, rootContext.options);
}

// src/composition.ts
function useFluent() {
  const instance = getCurrentInstance();
  assert(instance != null, "useFluent called outside of setup");
  const rootContext = inject(RootContextSymbol);
  assert(rootContext != null, "useFluent called without installing plugin");
  return getContext(rootContext, instance.proxy, true);
}

export {
  assert,
  warn,
  TranslationContext,
  getContext,
  getMergedContext,
  RootContextSymbol,
  useFluent
};
